// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import { ErrorCause, Feedback } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// props
export interface FormAddReconciliationItemValues {
	name: string;
}

export interface FormAddReconciliationItemProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

export const FormAddReconciliationItem = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddReconciliationItemProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState<FormAddReconciliationItemValues>({
		name: '',
	});

	// vars
	const idForm = 'form-add-reconciliation-item';

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// set loader
			if (setIsLoading) {
				setIsLoading(true);
			}

			const fetchAddReconciliationItem = await fetch(
				`${process.env.REACT_APP_API_URL}/reconciliation-items`,
				{
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify({
						name: values.name,
						project: project?.id,
					}),
				}
			);
			const resAddReconciliationItem = await fetchAddReconciliationItem.json();

			if (resAddReconciliationItem.error) {
				throw new Error(resAddReconciliationItem.error.message, {
					cause: {
						id: resAddReconciliationItem.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.reconciliationItemCreated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'RECONCILIATION_ITEM_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	return (
		<form id={idForm} name="formAddReconciliationItem" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
